var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/PluginServer.ts
import { Forbidden, Unauthorized } from "@hocuspocus/common";
import {
  HocuspocusProvider
} from "@hocuspocus/provider";
import { action, makeAutoObservable, reaction, runInAction } from "mobx";

// src/utils.ts
var shallowEqual = (obj1, obj2) => obj1 === obj2 || Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every((key) => Object.prototype.hasOwnProperty.call(obj2, key) && obj1[key] === obj2[key]);

// src/PluginServer.ts
import {
  Forbidden as Forbidden2,
  Unauthorized as Unauthorized2,
  ResetConnection,
  ConnectionTimeout
} from "@hocuspocus/common";
var PluginServer = class {
  constructor({
    attach = true,
    url,
    name,
    document,
    enabled = true,
    token,
    parameters = {},
    options = {}
  }) {
    this._disposer = null;
    this._connected = false;
    this._connecting = false;
    this._closeEvent = null;
    this._synced = false;
    this._enabled = true;
    this._provider = null;
    this.url = url;
    this.name = name;
    this.token = token;
    this._document = document;
    this._enabled = enabled;
    this._parameters = parameters;
    this._unsyncedChanges = 0;
    this.options = options;
    makeAutoObservable(this);
    if (attach)
      this.attach();
  }
  get connected() {
    return this._connected;
  }
  get connecting() {
    return this._connecting;
  }
  get synced() {
    return this._synced;
  }
  get enabled() {
    return this._enabled;
  }
  set enabled(enabled) {
    this._enabled = enabled;
  }
  get awareness() {
    var _a;
    return ((_a = this._provider) == null ? void 0 : _a.awareness) ?? null;
  }
  get provider() {
    return this._provider;
  }
  get document() {
    return this._document;
  }
  set document(document) {
    if (document !== this.document) {
      this._synced = false;
      this._document = document;
    }
  }
  get parameters() {
    return this._parameters;
  }
  set parameters(parameters) {
    if (!shallowEqual(this._parameters, parameters)) {
      this._parameters = parameters;
    }
  }
  get hasUnsyncedChanges() {
    return this._unsyncedChanges > 0;
  }
  get closed() {
    return this._closeEvent !== null;
  }
  get closeEvent() {
    return this._closeEvent;
  }
  get unauthorized() {
    var _a;
    return ((_a = this._closeEvent) == null ? void 0 : _a.code) === Unauthorized.code;
  }
  get forbidden() {
    var _a;
    return ((_a = this._closeEvent) == null ? void 0 : _a.code) === Forbidden.code;
  }
  dispose() {
    var _a;
    (_a = this._disposer) == null ? void 0 : _a.call(this);
    this._disposer = null;
  }
  attach() {
    if (this._disposer)
      return;
    const connectDisposer = reaction(() => {
      return {
        enabled: this.enabled,
        name: this.name,
        document: this.document,
        parameters: this.parameters,
        url: this.url,
        token: this.token
      };
    }, () => this._connect(), {
      fireImmediately: true
    });
    const optionsDisposer = reaction(() => {
      return {
        options: this.options
      };
    }, () => {
      if (this.provider)
        this.provider.setConfiguration(this.options);
    }, {
      fireImmediately: false
    });
    this._disposer = () => {
      connectDisposer();
      optionsDisposer();
    };
  }
  enable() {
    this._enabled = true;
  }
  disable() {
    this._enabled = false;
  }
  _connect() {
    runInAction(() => {
      var _a;
      (_a = this.provider) == null ? void 0 : _a.destroy();
      this._provider = null;
      this._closeEvent = null;
      if (!this.enabled || this.name === void 0 || this.document === void 0) {
        this._connecting = false;
        this._connected = false;
        return;
      }
      this._connecting = true;
      this._connected = false;
      const url = this.url.replace(/\/$/, "");
      const provider = new HocuspocusProvider(__spreadValues({
        url: `${this.url}/${this.name}`,
        name: this.name,
        document: this.document,
        parameters: this.parameters,
        token: this.token
      }, this.options));
      provider.on("unsyncedChanges", action(() => {
        this._unsyncedChanges = provider.unsyncedChanges;
      }));
      provider.on("synced", action(() => {
        this._connecting = false;
        this._connected = true;
        this._synced = true;
      }));
      provider.on("close", action(({ event }) => {
        this._closeEvent = event;
        this._connecting = false;
        this._connected = false;
      }));
      this._provider = provider;
    });
  }
};

// src/react.tsx
import { runInAction as runInAction2 } from "mobx";
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from "react";
var PluginServerContext = createContext(null);
function useServer() {
  const state = useContext(PluginServerContext);
  if (state === null)
    throw new Error("useServer() called outside of ServerProvider");
  return state;
}
function ServerProvider({
  children,
  options = {},
  url,
  document,
  name,
  token,
  enabled,
  parameters
}) {
  const [pluginServer] = useState(() => new PluginServer({
    attach: false,
    url,
    document,
    name,
    token,
    enabled,
    parameters,
    options
  }));
  useLayoutEffect(() => {
    pluginServer.attach();
    return () => {
      pluginServer.dispose();
    };
  }, [pluginServer]);
  const previousValues = useRef({
    url,
    document,
    name,
    token,
    enabled,
    parameters,
    options
  });
  useLayoutEffect(() => {
    runInAction2(() => {
      const newValues = {
        url,
        document,
        name,
        token,
        enabled,
        parameters: parameters ?? {},
        options: options ?? {}
      };
      Object.entries(newValues).forEach(([key, value]) => {
        if (value === previousValues.current[key]) {
          delete newValues[key];
        }
      });
      Object.assign(pluginServer, newValues);
      Object.assign(previousValues.current, newValues);
    });
  }, [pluginServer, url, document, name, token, enabled, parameters, options]);
  return /* @__PURE__ */ React.createElement(PluginServerContext.Provider, {
    value: pluginServer
  }, children);
}
export {
  ConnectionTimeout,
  Forbidden2 as Forbidden,
  PluginServer,
  ResetConnection,
  ServerProvider,
  Unauthorized2 as Unauthorized,
  useServer
};
